'use client';

/* eslint-disable react-hooks/rules-of-hooks */
import { useTranslations } from 'next-intl';
import { Controller } from 'react-hook-form';
import { cn } from '@/shared/lib/cn';
import { Icon } from '@/shared/ui/icon';
import '@/shared/ui/fields/field.scss';

import { PasswordField } from '@/shared/ui/fields/password-field';
import { FormFieldMessage } from './form-field-message';
import { FormFieldProps } from './types';
import { getFormFieldState } from './utils/getFormFieldState';
import { mergeRefs } from './utils/mergeRefs';
import { parseRulesForInputAttributes } from './utils/parseRulesForInputAttributes';

type CheckerConditionProps = {
    text: string;
    isValid: boolean;
};
const CheckerCondition = ({ text, isValid }: CheckerConditionProps) => {
    return (
        <div className={cn('field__checker-condition', isValid && 'field__checker-condition--valid')}>
            <Icon name="check" />
            <span>{text}</span>
        </div>
    );
};

const UPPER_AND_LOWER_REGEX = /(?=.*[a-z])(?=.*[A-Z])/;
const SPECIAL_CHAR_REGEX = /[!@#$%^&*(),.?":{}|<>+\/\\"`'_=\[\];~-]/;

type Props = FormFieldProps<typeof PasswordField> & {
    isCheckerEnabled?: boolean;
};
export const FormPasswordField = ({
    name,
    control,
    defaultValue,
    disabled,
    rules,
    shouldUnregister,
    messageWrapperProps,
    isErrorVisible = true,
    isSuccessVisible,
    ref,
    isCheckerEnabled,
    ...props
}: Props) => {
    const customRules = {
        ...rules,
        ...(isCheckerEnabled && {
            minLength: 8,
            validate: {
                ...rules?.validate,
                upperAndLower: (value: string) => UPPER_AND_LOWER_REGEX.test(value),
                specialChar: (value: string) => SPECIAL_CHAR_REGEX.test(value),
            },
        }),
    };
    return (
        <Controller
            {...{ name, control, defaultValue, disabled, rules: customRules, shouldUnregister }}
            render={({
                field: { ref: fieldRef, ...field },
                fieldState: { error, isDirty, isTouched, invalid },
            }) => {
                const t = useTranslations();
                const state = getFormFieldState({
                    invalid,
                    isDirty,
                    isTouched,
                    isErrorVisible,
                    isSuccessVisible,
                });

                return (
                    <div>
                        <PasswordField
                            ref={mergeRefs([fieldRef, ref])}
                            state={state}
                            {...parseRulesForInputAttributes(customRules)}
                            {...field}
                            {...props}
                        />
                        {isCheckerEnabled && (
                            <div className="field__checker">
                                <span>{t('PASSWORD_CHECKER.TITLE')}</span>
                                <CheckerCondition
                                    text={t('PASSWORD_CHECKER.MIN_LENGTH')}
                                    isValid={field.value.length >= 8}
                                />
                                <CheckerCondition
                                    text={t('PASSWORD_CHECKER.UPPER_AND_LOWER')}
                                    isValid={UPPER_AND_LOWER_REGEX.test(field.value)}
                                />
                                <CheckerCondition
                                    text={t('PASSWORD_CHECKER.SPECIAL_CHAR')}
                                    isValid={SPECIAL_CHAR_REGEX.test(field.value)}
                                />
                            </div>
                        )}
                        <FormFieldMessage
                            state={state}
                            error={error}
                            messageWrapperProps={messageWrapperProps}
                        />
                    </div>
                );
            }}
        />
    );
};
